/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * dictationParser — rules-based parser that converts free-form medical
 * dictation (Italian / English / Spanish) into a structured prescription line.
 *
 *   IT: "amoxicillina 500 milligrammi due volte al giorno per sette giorni"
 *   EN: "amoxicillin 500 milligrams twice a day for seven days"
 *   ES: "amoxicilina 500 miligramos dos veces al día durante siete días"
 *   → { name: 'amoxicillina', dosage: '500 mg', posology: '2 volte al giorno',
 *        duration: '7 giorni', quantity: 1 }
 *
 * The parser output is normalised to the Italian labels used across the
 * PrescriptionCreator UI, so the doctor sees a consistent form regardless of
 * the dictation language. If parsing fails we still expose the raw transcript
 * as `name` so nothing is lost — the doctor edits the fields before signing.
 */

// ---------- Numeric lexicons per language ----------
const NUMBERS = {
  it: {
    zero: 0, uno: 1, una: 1, un: 1, due: 2, tre: 3, quattro: 4, cinque: 5, sei: 6,
    sette: 7, otto: 8, nove: 9, dieci: 10, undici: 11, dodici: 12, quindici: 15,
    venti: 20, trenta: 30, quaranta: 40, cinquanta: 50, sessanta: 60,
    cento: 100, duecento: 200, cinquecento: 500, mille: 1000,
  },
  en: {
    zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7,
    eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14,
    fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
    twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60,
    seventy: 70, eighty: 80, ninety: 90, hundred: 100, thousand: 1000,
  },
  es: {
    cero: 0, uno: 1, una: 1, un: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6,
    siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, trece: 13,
    catorce: 14, quince: 15, veinte: 20, treinta: 30, cuarenta: 40,
    cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80, noventa: 90,
    cien: 100, ciento: 100, doscientos: 200, quinientos: 500, mil: 1000,
  },
};

// ---------- Unit dictionaries per language (all normalise to IT-style symbols) ----------
const UNITS = {
  it: {
    'milligrammi': 'mg', 'milligrammo': 'mg', 'mg': 'mg',
    'grammi': 'g', 'grammo': 'g', 'g': 'g',
    'microgrammi': 'mcg', 'microgrammo': 'mcg', 'mcg': 'mcg', 'ug': 'mcg',
    'unità': 'UI', 'ui': 'UI', 'unita': 'UI',
    'millilitri': 'ml', 'ml': 'ml',
    'gocce': 'gtt', 'gtt': 'gtt',
    'compresse': 'cpr', 'compressa': 'cpr', 'cpr': 'cpr',
    'capsule': 'cps', 'capsula': 'cps', 'cps': 'cps',
    'fiale': 'fl', 'fiala': 'fl',
    'bustine': 'bust', 'bustina': 'bust',
  },
  en: {
    'milligrams': 'mg', 'milligram': 'mg', 'mg': 'mg',
    'grams': 'g', 'gram': 'g', 'g': 'g',
    'micrograms': 'mcg', 'microgram': 'mcg', 'mcg': 'mcg', 'ug': 'mcg',
    'units': 'UI', 'unit': 'UI', 'iu': 'UI',
    'milliliters': 'ml', 'millilitres': 'ml', 'ml': 'ml',
    'drops': 'gtt', 'drop': 'gtt',
    'tablets': 'cpr', 'tablet': 'cpr', 'tabs': 'cpr', 'tab': 'cpr',
    'capsules': 'cps', 'capsule': 'cps', 'caps': 'cps',
    'vials': 'fl', 'vial': 'fl',
    'sachets': 'bust', 'sachet': 'bust',
  },
  es: {
    'miligramos': 'mg', 'miligramo': 'mg', 'mg': 'mg',
    'gramos': 'g', 'gramo': 'g', 'g': 'g',
    'microgramos': 'mcg', 'microgramo': 'mcg', 'mcg': 'mcg',
    'unidades': 'UI', 'unidad': 'UI', 'ui': 'UI',
    'mililitros': 'ml', 'ml': 'ml',
    'gotas': 'gtt', 'gota': 'gtt', 'gtt': 'gtt',
    'comprimidos': 'cpr', 'comprimido': 'cpr', 'cpr': 'cpr',
    'cápsulas': 'cps', 'capsulas': 'cps', 'cápsula': 'cps', 'capsula': 'cps', 'cps': 'cps',
    'ampollas': 'fl', 'ampolla': 'fl',
    'sobres': 'bust', 'sobre': 'bust',
  },
};

// ---------- Language auto-detection (light heuristic) ----------
const LANG_HINTS = {
  it: ['al giorno', 'volte', 'giorni', 'giorno', 'ogni', 'compresse', 'compressa', 'grammi', 'gocce', 'milligrammi', 'bisogno', 'mattino', 'sera'],
  en: ['a day', 'per day', 'twice', 'once', 'times', 'days', 'every', 'tablets', 'tablet', 'drops', 'hours', 'as needed', 'prn', 'milligrams', 'weeks', 'bedtime', 'morning'],
  es: ['al día', 'al dia', 'veces', 'días', 'dias', 'cada', 'comprimidos', 'gotas', 'horas', 'durante', 'miligramos', 'demanda', 'noche', 'mañana'],
};

const detectLang = (text) => {
  const scores = { it: 0, en: 0, es: 0 };
  const lower = ` ${text.toLowerCase()} `;
  for (const [lang, hints] of Object.entries(LANG_HINTS)) {
    for (const h of hints) {
      if (lower.includes(` ${h} `) || lower.includes(` ${h}.`) || lower.includes(` ${h},`)) {
        scores[lang] += 1;
      }
    }
  }
  const best = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : null;
};

const normalizeText = (raw) => (raw || '')
  .toLowerCase()
  .replace(/[.,;!?]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const wordToNumber = (word, lang) => {
  if (word == null) return NaN;
  if (typeof word === 'number') return word;
  const trimmed = String(word).trim();
  if (/^\d+(?:[.,]\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed.replace(',', '.'));
  }
  const dict = NUMBERS[lang] || NUMBERS.it;
  return dict[trimmed] ?? NaN;
};

// ---------- Posology extraction (per language, normalised to IT labels) ----------
const extractPosology = (text, lang) => {
  if (lang === 'en') {
    // "twice a day" / "three times a day" / "3 times per day"
    let m = text.match(/\b(once|twice|thrice|one|two|three|four|five|six|\d+)\s+(?:times?)?\s*(?:a|per)\s+day\b/i);
    if (m) {
      const map = { once: 1, twice: 2, thrice: 3, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 };
      const raw = m[1].toLowerCase();
      const n = map[raw] ?? parseFloat(raw);
      if (!Number.isNaN(n)) return `${n} volte al giorno`;
    }
    m = text.match(/\bevery\s+(\d+|one|two|three|four|six|eight|twelve)\s+hours?\b/i);
    if (m) {
      const map = { one: 1, two: 2, three: 3, four: 4, six: 6, eight: 8, twelve: 12 };
      const raw = m[1].toLowerCase();
      const n = map[raw] ?? parseFloat(raw);
      if (!Number.isNaN(n)) return `Ogni ${n} ore`;
    }
    if (/\bas needed\b|\bprn\b/.test(text)) return 'Al bisogno';
    if (/\bin the morning\b/.test(text)) return '1 volta al giorno (mattino)';
    if (/\bat night\b|\bat bedtime\b/.test(text)) return '1 volta al giorno (sera)';
    return '';
  }
  if (lang === 'es') {
    // "dos veces al día" / "3 veces al día" / "cada 8 horas"
    let m = text.match(/\b(uno|una|un|dos|tres|cuatro|cinco|seis|\d+)\s+veces?\s+al\s+d[ií]a\b/i);
    if (m) {
      const n = wordToNumber(m[1], 'es');
      if (!Number.isNaN(n)) return `${n} volte al giorno`;
    }
    m = text.match(/\bcada\s+(\d+|una|dos|tres|cuatro|seis|ocho|doce)\s+horas?\b/i);
    if (m) {
      const n = wordToNumber(m[1], 'es');
      if (!Number.isNaN(n)) return `Ogni ${n} ore`;
    }
    if (/\ba demanda\b|\bsi es necesario\b/.test(text)) return 'Al bisogno';
    if (/\bpor la mañana\b|\ben la mañana\b/.test(text)) return '1 volta al giorno (mattino)';
    if (/\bpor la noche\b|\ben la noche\b/.test(text)) return '1 volta al giorno (sera)';
    return '';
  }
  // Italian (default)
  let m = text.match(/\b(?:una|uno|un|due|tre|quattro|cinque|sei|\d+)\s+(?:volte?|volta)\s+al?\s+(?:giorno|dì|di)/i);
  if (m) {
    const n = wordToNumber(m[0].split(/\s+/)[0], 'it');
    if (!Number.isNaN(n)) return `${n} volte al giorno`;
  }
  m = text.match(/\bogni\s+(\d+|una|due|tre|quattro|sei|otto|dodici)\s+ore?\b/i);
  if (m) {
    const n = wordToNumber(m[1], 'it');
    if (!Number.isNaN(n)) return `Ogni ${n} ore`;
  }
  if (/\ba\s+bisogno\b/.test(text)) return 'Al bisogno';
  if (/\bal\s+mattino\b/.test(text)) return '1 volta al giorno (mattino)';
  if (/\ba\s+cena\b/.test(text)) return '1 volta al giorno (sera)';
  return '';
};

// ---------- Dosage extraction ----------
const extractDosage = (text, lang) => {
  const unitDict = UNITS[lang] || UNITS.it;
  const unitKeys = Object.keys(unitDict).sort((a, b) => b.length - a.length);
  const unitPattern = unitKeys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  // number-word alternation per language
  const numWords = Object.keys(NUMBERS[lang] || NUMBERS.it).join('|');
  const re = new RegExp(
    `(\\d+(?:[.,]\\d+)?|${numWords})\\s+(${unitPattern})\\b`,
    'i',
  );
  const m = text.match(re);
  if (!m) return '';
  const value = wordToNumber(m[1], lang);
  if (Number.isNaN(value)) return '';
  const unit = unitDict[m[2].toLowerCase()] || m[2];
  const display = Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
  return `${display} ${unit}`;
};

// ---------- Duration extraction ----------
const extractDuration = (text, lang) => {
  if (lang === 'en') {
    const m = text.match(/\bfor\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten|fourteen|fifteen|twenty|thirty)\s+(days?|weeks?|months?)\b/i);
    if (!m) return '';
    const n = wordToNumber(m[1], 'en');
    const unit = m[2].toLowerCase();
    const unitLabel = unit.startsWith('week') ? 'settimane' : unit.startsWith('month') ? 'mesi' : 'giorni';
    return Number.isNaN(n) ? '' : `${n} ${unitLabel}`;
  }
  if (lang === 'es') {
    const m = text.match(/\b(?:durante|por)\s+(\d+|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|quince|veinte|treinta)\s+(d[ií]as?|semanas?|meses?)\b/i);
    if (!m) return '';
    const n = wordToNumber(m[1], 'es');
    const unit = m[2].toLowerCase();
    const unitLabel = unit.startsWith('sem') ? 'settimane' : unit.startsWith('mes') ? 'mesi' : 'giorni';
    return Number.isNaN(n) ? '' : `${n} ${unitLabel}`;
  }
  // Italian
  const m = text.match(/\bper\s+(\d+|uno|due|tre|quattro|cinque|sei|sette|otto|nove|dieci|undici|dodici|quindici|venti|trenta)\s+(giorni|giorno|settimane|settimana|mesi|mese)\b/i);
  if (!m) return '';
  const n = wordToNumber(m[1], 'it');
  const unit = m[2].toLowerCase();
  const unitLabel = unit.startsWith('sett') ? 'settimane' : unit.startsWith('mes') ? 'mesi' : 'giorni';
  return Number.isNaN(n) ? '' : `${n} ${unitLabel}`;
};

// ---------- Quantity (# of packs / boxes) ----------
const extractQuantity = (text, lang) => {
  let re;
  if (lang === 'en') {
    re = /\b(\d+|one|two|three|four|five)\s+(?:packs?|boxes|box)\b/i;
  } else if (lang === 'es') {
    re = /\b(\d+|uno|una|un|dos|tres|cuatro|cinco)\s+(?:cajas?|envases?|paquetes?)\b/i;
  } else {
    re = /\b(\d+|uno|una|un|due|tre|quattro|cinque)\s+(?:confezioni?|scatole?|pezzi?)\b/i;
  }
  const m = text.match(re);
  if (!m) return null;
  const n = wordToNumber(m[1], lang);
  return Number.isNaN(n) ? null : n;
};

// ---------- Drug name extraction ----------
const extractName = (text, lang) => {
  const commonMarkers = [/\d+/, /\bmg\b/i, /\bml\b/i, /\bg\b/i];
  const langMarkers = {
    it: [/milligrammi?/i, /grammi?/i, /gocce/i, /compresse?/i, /capsule?/i, /volte?/i, /ogni/i, /per\b/i],
    en: [/milligrams?/i, /grams?/i, /drops?/i, /tablets?/i, /capsules?/i, /times?/i, /every/i, /\bfor\b/i, /twice/i, /once/i],
    es: [/miligramos?/i, /gramos?/i, /gotas?/i, /comprimidos?/i, /c[áa]psulas?/i, /veces?/i, /cada/i, /durante/i, /por\b/i],
  };
  const markers = [...commonMarkers, ...(langMarkers[lang] || langMarkers.it)];
  let cut = text.length;
  for (const re of markers) {
    const m = re.exec(text);
    if (m && m.index < cut) cut = m.index;
  }
  const stripVerbs = {
    it: /^(prescriv|somministr|dò)i?\s+/i,
    en: /^(prescribe|administer|give)s?\s+/i,
    es: /^(recet|prescrib|administr|d[oa])[a-z]*\s+/i,
  };
  return text.slice(0, cut).trim().replace(stripVerbs[lang] || stripVerbs.it, '');
};

/**
 * Public API.
 *
 * @param {string} rawText — the raw dictation transcript
 * @param {object} [opts]
 * @param {'it'|'en'|'es'|'auto'} [opts.lang='auto'] — hint the parser language.
 *   When 'auto' (default) the parser auto-detects using simple heuristics and
 *   falls back to Italian if the text is ambiguous.
 */
export const parseDictation = (rawText, opts = {}) => {
  const text = normalizeText(rawText);
  if (!text) {
    return { name: '', dosage: '', posology: '', duration: '', quantity: null, raw: rawText, lang: null };
  }
  const requested = opts.lang && opts.lang !== 'auto' ? opts.lang : null;
  const detected = requested || detectLang(text) || 'it';

  const name = extractName(text, detected);
  const dosage = extractDosage(text, detected);
  const posology = extractPosology(text, detected);
  const duration = extractDuration(text, detected);
  const quantity = extractQuantity(text, detected);
  return { name, dosage, posology, duration, quantity, raw: rawText, lang: detected };
};

// Map UI-facing lang codes to the BCP-47 tags the browser's SpeechRecognition
// expects. Exposed so the DictationButton can drive its recognizer without
// having to duplicate the mapping.
export const SPEECH_LANG_TAGS = {
  it: 'it-IT',
  en: 'en-US',
  es: 'es-ES',
};

export default parseDictation;
